// The engagement frame (Workspace 2): loads the engagement and its workspace tables once, renders the current screen and the
// collapsible chat panel on the right — scoped to the workpaper on a workpaper page, to the client documents on Client docs,
// and to the engagement elsewhere. On a phone the chat opens as a drawer from a floating button. "Engine details" (the
// earlier tabs) render without the chat, full width.
import { useState } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { Button, Drawer, ErrorState, Icon } from "../../components";
import { usePref } from "../../lib/hooks";
import { EngagementProvider, useEngagement } from "../engagement/Layout";
import { ChatPanel, type ChatScope } from "./Chat";
import { WsProvider, useWs } from "./common";

export function WorkspaceLayout() {
  const { id = "" } = useParams();
  return <EngagementProvider key={id} id={id}><WithTables /></EngagementProvider>;
}

function WithTables() {
  const { id, model } = useEngagement();
  return <WsProvider id={id} header={model.header} team={model.team}><Frame /></WsProvider>;
}

/** Which conversation the chat shows for the current path. */
export function chatScopeFor(pathname: string, ws: { id: string; wp: Array<{ ref: string; workpaperId: string | null; name: string }>; attention?: Array<{ key: string; ref: string | null; workpaperId: string | null }> }): ChatScope {
  const rest = pathname.replace(/^\/e\/[^/]+/, "");
  // an issue or a tailoring review: the conversation of its workpaper (the engine's item names it), else the engagement's
  const detail = rest.match(/^\/issues\/([^/]+)/) ?? rest.match(/^\/tailoring\/([^/]+)/);
  if (detail) {
    const k = decodeURIComponent(detail[1]);
    const item = ws.attention?.find((a) => a.key === `iss:${k}`);
    const row = item?.ref ? ws.wp.find((w) => w.ref === item.ref) : ws.wp.find((w) => w.workpaperId === k);
    if (row) return { scope: "workpaper", ref: row.ref, label: `${row.ref} ${row.name}` };
    return { scope: "engagement", ref: null, label: "This engagement" };
  }
  const wp = rest.match(/^\/wp\/([^/]+)/);
  if (wp) {
    const key = decodeURIComponent(wp[1]);
    const row = ws.wp.find((w) => w.ref === key) ?? ws.wp.find((w) => w.workpaperId === key);
    return { scope: "workpaper", ref: row?.ref ?? key, label: row ? `${row.ref} ${row.name}` : key };
  }
  if (rest.startsWith("/documents")) return { scope: "pbc", ref: null, label: "Client documents" };
  return { scope: "engagement", ref: null, label: "This engagement" };
}

function Frame() {
  const ws = useWs();
  const { pathname } = useLocation();
  const [open, setOpen] = usePref("chat-open", true);
  const [phone, setPhone] = useState(false);
  if (/^\/e\/[^/]+\/details(\/|$)/.test(pathname)) return <div className="mx-auto max-w-7xl"><Outlet /></div>;
  const scope = chatScopeFor(pathname, ws);
  return (
    <div className="mx-auto flex max-w-[1560px] items-start gap-5">
      <div className="min-w-0 flex-1 space-y-4">
        {ws.error && <ErrorState error={`The workspace lists could not load: ${ws.error}`} onRetry={ws.reload} />}
        <Outlet />
      </div>
      {open ? (
        <aside aria-label="Chat panel" className="sticky top-0 hidden h-[calc(100vh-5.5rem)] w-80 shrink-0 flex-col overflow-hidden rounded-lg border border-line bg-raised lg:flex xl:w-96">
          <ChatPanel key={`${scope.scope}:${scope.ref}`} scope={scope} onHide={() => setOpen(false)} />
        </aside>
      ) : (
        <button type="button" onClick={() => setOpen(true)} aria-label="Show the chat"
          className="sticky top-0 hidden items-center gap-1.5 rounded-lg border border-line bg-raised px-2 py-3 text-xs text-muted hover:text-ink lg:flex lg:flex-col">
          <Icon name="chat" size={16} /><span className="[writing-mode:vertical-rl]">Chat</span>
        </button>
      )}
      <Button kind="primary" className="fixed bottom-4 right-4 z-20 shadow-pop lg:hidden" onClick={() => setPhone(true)} aria-label={`Open the chat on ${scope.label}`}>
        <Icon name="chat" size={16} />Chat
      </Button>
      <Drawer open={phone} onClose={() => setPhone(false)} title="Chat">
        <div className="-m-4 h-[calc(100vh-4rem)]"><ChatPanel key={`p:${scope.scope}:${scope.ref}`} scope={scope} /></div>
      </Drawer>
    </div>
  );
}

/** Earlier engagement URLs (/engagements/:id/...) → where that content lives now. */
const OLD_TAB: Record<string, string> = {
  "": "", file: "folders", flow: "workpapers", evidence: "documents", client: "documents", agents: "actions", health: "actions",
  planning: "details/planning", areas: "details/areas", "single-audit": "details/single-audit", completion: "details/completion", release: "details/release",
};
export function OldEngagementRedirect() {
  const { id = "", "*": rest = "" } = useParams();
  const wp = rest.match(/^wp\/(.+)$/);
  const to = wp ? `wp/${wp[1]}` : OLD_TAB[rest.replace(/\/$/, "")] ?? "";
  return <Navigate to={`/e/${id}${to ? "/" + to : ""}`} replace />;
}
